import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserGroupIcon,
  BanknotesIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowPathIcon,
  EyeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import EmployeesTab from '../components/wage-protection/EmployeesTab';
import BatchesTab from '../components/wage-protection/BatchesTab';
import ReportsTab from '../components/wage-protection/ReportsTab';
import DashboardLayout from '../components/dashboard/DashboardLayout';

// Types and Interfaces
interface Employee {
  id: string;
  name: string;
  employeeId: string;
  department: string;
  position: string;
  salary: number;
  basicSalary: number;
  allowances: number;
  deductions: number;
  netSalary: number;
  contractType: 'permanent' | 'temporary' | 'contract';
  bankAccount: string;
  paymentStatus: 'paid' | 'pending' | 'overdue' | 'processing';
  lastPaymentDate: string;
  joinDate: string;
}

interface PaymentBatch {
  id: string;
  batchNumber: string;
  totalEmployees: number;
  totalAmount: number;
  status: 'draft' | 'submitted' | 'approved' | 'processed' | 'completed' | 'rejected';
  submissionDate: string;
  processingDate?: string;
  completionDate?: string;
  submittedBy: string;
  approvedBy?: string;
  rejectionReason?: string;
  description?: string;
  paymentMethod: 'bank_transfer' | 'cash' | 'check';
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface ReportData {
  totalPayments: number;
  totalAmount: number;
  averageProcessingTime: number;
  complianceRate: number;
  monthlyTrend: number;
  topDepartments: Array<{
    name: string;
    employees: number;
    amount: number;
  }>;
  paymentsByStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  monthlyPayments: Array<{
    month: string;
    payments: number;
    amount: number;
  }>;
}

const WageProtectionPage: React.FC = () => {

  const [activeTab, setActiveTab] = useState<'overview' | 'employees' | 'batches' | 'reports'>('overview');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<PaymentBatch | null>(null);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(false);

  // Mock data for employees
  const mockEmployees: Employee[] = [
    {
      id: '1',
      name: 'أحمد محمد العلي',
      employeeId: '1234567890',
      department: 'تقنية المعلومات',
      position: 'مطور برمجيات أول',
      salary: 17000,
      basicSalary: 15000,
      allowances: 2500,
      deductions: 500,
      netSalary: 17000,
      contractType: 'permanent',
      bankAccount: 'SA1234567890123456789012',
      paymentStatus: 'paid',
      lastPaymentDate: '2024-01-30',
      joinDate: '2022-03-15'
    },
    {
      id: '2',
      name: 'فاطمة سالم الزهراني',
      employeeId: '2345678901',
      department: 'المالية',
      position: 'محاسبة رئيسية',
      salary: 13500,
      basicSalary: 12000,
      allowances: 1800,
      deductions: 300,
      netSalary: 13500,
      contractType: 'permanent',
      bankAccount: 'SA2345678901234567890123',
      paymentStatus: 'pending',
      lastPaymentDate: '2024-01-30',
      joinDate: '2021-07-20'
    },
    {
      id: '3',
      name: 'خالد عبدالله النمر',
      employeeId: '3456789012',
      department: 'إدارة المشاريع',
      position: 'مدير مشروع',
      salary: 20200,
      basicSalary: 18000,
      allowances: 3000,
      deductions: 800,
      netSalary: 20200,
      contractType: 'permanent',
      bankAccount: 'SA3456789012345678901234',
      paymentStatus: 'overdue',
      lastPaymentDate: '2024-01-15',
      joinDate: '2020-11-10'
    }
  ];

  // Mock data for batches
  const mockBatches: PaymentBatch[] = [
    {
      id: '1',
      batchNumber: 'WP-2024-001',
      totalEmployees: 45,
      totalAmount: 225000,
      status: 'completed',
      submissionDate: '2024-01-15',
      completionDate: '2024-01-18',
      submittedBy: 'أحمد محمد',
      approvedBy: 'مدير الموارد البشرية',
      description: 'دفعة رواتب شهر يناير - القسم الإداري',
      paymentMethod: 'bank_transfer',
      priority: 'medium'
    },
    {
      id: '2',
      batchNumber: 'WP-2024-002',
      totalEmployees: 32,
      totalAmount: 160000,
      status: 'processed',
      submissionDate: '2024-01-20',
      processingDate: '2024-01-21',
      submittedBy: 'فاطمة علي',
      description: 'دفعة رواتب شهر يناير - القسم التقني',
      paymentMethod: 'bank_transfer',
      priority: 'high'
    },
    {
      id: '3',
      batchNumber: 'WP-2024-003',
      totalEmployees: 28,
      totalAmount: 140000,
      status: 'submitted',
      submissionDate: '2024-01-22',
      submittedBy: 'محمد الأحمد',
      description: 'دفعة رواتب شهر يناير - قسم المبيعات',
      paymentMethod: 'bank_transfer',
      priority: 'medium'
    },
    {
      id: '4',
      batchNumber: 'WP-2024-004',
      totalEmployees: 15,
      totalAmount: 75000,
      status: 'draft',
      submissionDate: '2024-01-25',
      submittedBy: 'سارة أحمد',
      description: 'دفعة رواتب شهر يناير - قسم التسويق',
      paymentMethod: 'bank_transfer',
      priority: 'low'
    }
  ];

  // Mock report data
  const mockReportData: ReportData = {
    totalPayments: 1250,
    totalAmount: 6250000,
    averageProcessingTime: 3.2,
    complianceRate: 98.5,
    monthlyTrend: 12.5,
    topDepartments: [
      { name: 'الإدارة العامة', employees: 45, amount: 450000 },
      { name: 'التقنية', employees: 32, amount: 320000 },
      { name: 'المبيعات', employees: 28, amount: 280000 },
      { name: 'التسويق', employees: 15, amount: 150000 },
      { name: 'الموارد البشرية', employees: 12, amount: 120000 }
    ],
    paymentsByStatus: [
      { status: 'completed', count: 850, percentage: 68 },
      { status: 'processed', count: 200, percentage: 16 },
      { status: 'pending', count: 150, percentage: 12 },
      { status: 'rejected', count: 50, percentage: 4 }
    ],
    monthlyPayments: [
      { month: 'يناير 2024', payments: 120, amount: 600000 },
      { month: 'ديسمبر 2023', payments: 115, amount: 575000 },
      { month: 'نوفمبر 2023', payments: 110, amount: 550000 },
      { month: 'أكتوبر 2023', payments: 108, amount: 540000 },
      { month: 'سبتمبر 2023', payments: 105, amount: 525000 },
      { month: 'أغسطس 2023', payments: 102, amount: 510000 }
    ]
  };

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'completed':
      case 'approved':
        return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'pending':
      case 'submitted':
      case 'processing':
        return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
      case 'overdue':
      case 'rejected':
        return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'draft':
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
      default:
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
      case 'completed':
      case 'approved':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'pending':
      case 'submitted':
      case 'processing':
        return <ClockIcon className="w-4 h-4" />;
      case 'overdue':
      case 'rejected':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'draft':
        return <DocumentTextIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter employees based on search and status
  const filteredEmployees = mockEmployees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || employee.paymentStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout currentPage="wage-protection">
      <div className="bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  مدد - حماية الأجور
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  نظام إدارة وحماية أجور الموظفين
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateBatch(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <PlusIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                دفعة جديدة
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsLoading(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-600"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2 ${isLoading ? 'animate-spin' : ''}`} />
                تحديث
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 rtl:space-x-reverse" aria-label="Tabs">
            {[
              { id: 'overview', name: 'نظرة عامة', icon: ChartBarIcon },
              { id: 'employees', name: 'الموظفون', icon: UserGroupIcon },
              { id: 'batches', name: 'الدفعات', icon: DocumentTextIcon },
              { id: 'reports', name: 'التقارير', icon: DocumentTextIcon }
            ].map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ y: -1 }}
                onClick={() => setActiveTab(tab.id as any)}
                className={`${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600 dark:text-green-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 rtl:space-x-reverse transition-all duration-200`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </motion.button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <UserGroupIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="mr-4 rtl:mr-0 rtl:ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        إجمالي الموظفين
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {mockEmployees.length.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <BanknotesIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="mr-4 rtl:mr-0 rtl:ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        إجمالي الرواتب الشهرية
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(mockEmployees.reduce((acc, employee) => acc + employee.salary, 0))}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                      <ClockIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div className="mr-4 rtl:mr-0 rtl:ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        المدفوعات المعلقة
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {mockEmployees.filter(employee => employee.paymentStatus === 'pending').length}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="mr-4 rtl:mr-0 rtl:ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        المدفوعات المتأخرة
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {mockEmployees.filter(employee => employee.paymentStatus === 'overdue').length}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Compliance and Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-green-500 mr-2 rtl:mr-0 rtl:ml-2" />
                    معدل الامتثال
                  </h3>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {mockReportData.complianceRate}%
                    </span>
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
                      <span className="text-sm font-medium">+2.3%</span>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${mockReportData.complianceRate}%` }}
                      transition={{ duration: 1.5, delay: 0.7 }}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full"
                    />
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                    معدل امتثال ممتاز لمتطلبات حماية الأجور
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <ClockIcon className="h-5 w-5 text-blue-500 mr-2 rtl:mr-0 rtl:ml-2" />
                    متوسط وقت المعالجة
                  </h3>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {mockReportData.averageProcessingTime} يوم
                    </span>
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <ArrowTrendingDownIcon className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
                      <span className="text-sm font-medium">-0.5 يوم</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">الهدف: 3 أيام</span>
                      <span className="text-green-600 dark:text-green-400 font-medium">تحت الهدف</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                    أداء ممتاز في سرعة معالجة المدفوعات
                  </p>
                </motion.div>
              </div>

              {/* Recent Activity */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-gray-500 mr-2 rtl:mr-0 rtl:ml-2" />
                    آخر الدفعات
                  </h3>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {mockBatches.slice(0, 3).map((batch, index) => (
                      <motion.div
                        key={batch.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                        onClick={() => setSelectedBatch(batch)}
                      >
                        <div className="flex items-center space-x-4 rtl:space-x-reverse">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(batch.status)}`}></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {batch.batchNumber}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {batch.totalEmployees} موظف • {formatCurrency(batch.totalAmount)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {formatDate(batch.submissionDate)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {batch.submittedBy}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="mt-6 text-center">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setActiveTab('batches')}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-600"
                    >
                      <EyeIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                      عرض جميع الدفعات
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'overview' && (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                محتوى نظرة عامة - تم عرضه أعلاه
              </p>
            </div>
          )}
          
          {activeTab === 'employees' && (
            <EmployeesTab
              employees={mockEmployees}
              selectedEmployee={selectedEmployee}
              setSelectedEmployee={setSelectedEmployee}
            />
          )}
          
          {activeTab === 'batches' && (
            <BatchesTab
              batches={mockBatches}
              selectedBatch={selectedBatch}
              setSelectedBatch={setSelectedBatch}
              showCreateBatch={showCreateBatch}
              setShowCreateBatch={setShowCreateBatch}
            />
          )}
          
          {activeTab === 'reports' && (
            <ReportsTab reportData={mockReportData} />
          )}
        </div>
      </div>
      </div>
    </DashboardLayout>
  );
};

export default WageProtectionPage;
